import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { AppState } from 'src/redux/store';
import { useAppSelector, useAppDispatch } from 'src/redux/hooks';
import {
  NOTIFICATIONS,
  PREFERENCES,
  HOME_PAGE,
  THREAD_LISTING,
  GROUPS,
  pagePathMap,
} from 'src/constants/pages';
import { ProfileStatus } from 'src/types/UserInfo';
import { isGroupsVisibleForMpId, useMetrics, useTranslation } from 'src/utils';
import FetchStatus from 'src/types/FetchStatus';
import { fetchNotificationCount } from 'src/redux/slices/notifications';
import { ButtonTypes } from 'src/constants/metrics';
import MarketplaceSwitcher from 'src/components/MarketplaceSwitcher';
import { KatBadge } from '@amzn/katal-react';
import { NavigationContainerLayout } from 'src/components/layouts/navigation/NavigationContainer';
import SearchBar from 'src/components/navigation/SearchBar';
import PageNavigation from './PageNavigation';

const AppNavigation: React.FC = () => {
  const { t } = useTranslation();
  const { clickMetric } = useMetrics();
  const dispatch = useAppDispatch();
  const unreadCount = useAppSelector(({ notifications }: AppState) => notifications.unreadCount);
  const profileStatus = useAppSelector(({ userInfo }: AppState) => userInfo.profileStatus);
  const notificationCountStatus = useAppSelector(({ notifications }: AppState) => notifications
    .notificationCountStatus);
  const selectedMarketplaceId = useAppSelector(
    ({ userInfo }: AppState) => userInfo.selectedMarketplaceId,
  );
  const { pathname } = useRouter();
  const requestedPath = `/${pathname.split('/')[1]}`;
  useEffect(() => {
    if (profileStatus === ProfileStatus.registered
          && notificationCountStatus !== FetchStatus.complete
    ) {
      dispatch(fetchNotificationCount());
    }
  }, [profileStatus]);

  const authenticatedTabs = [
    {
      label: unreadCount ? (
        <>
          {t('pages_notifications')}
          {' '}
          <KatBadge type="info" label={unreadCount > 99 ? '99+' : unreadCount.toString()} />
        </>
      ) : t('pages_notifications'),
      href: NOTIFICATIONS,
    },
    {
      label: t('pages_preferences'),
      href: PREFERENCES,
    },
  ];

  const navbarTabs: { label: React.ReactNode, href: string }[] = [
    {
      label: t('pages_home'),
      href: HOME_PAGE,
    },
    {
      label: t('pages_discussions'),
      href: THREAD_LISTING,
    },
  ];

  if (isGroupsVisibleForMpId(selectedMarketplaceId)) {
    navbarTabs.push({
      label: t('pages_groups'),
      href: GROUPS,
    });
  }

  if (profileStatus === ProfileStatus.registered) {
    navbarTabs.push(...authenticatedTabs);
  }

  return (
    <NavigationContainerLayout
      appName={t('page_title_home')}
      pageNavigation={(
        <PageNavigation
          name={t('forums')}
          links={navbarTabs.map(({ href, label }) => ({
            href,
            label,
            isSelected: requestedPath === href,
            onClick: () => {
              clickMetric({
                metricName: ButtonTypes.navbar,
                metricValue: pagePathMap[href],
              });
            },
          }))}
        />
      )}
      marketplacePicker={<MarketplaceSwitcher />}
      searchBar={<SearchBar />}
    />

  );
};

export default AppNavigation;
